import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";

interface SubscriptionStatus {
  isExpired: boolean;
  hasNoSubscription: boolean;
  canAccessFeatures: boolean;
  isTrialing: boolean;
  isAwaitingTrialPayment: boolean;
  isPartnerGated: boolean;
  isLoading: boolean;
}

export const useSubscriptionStatus = (): SubscriptionStatus => {
  const { workspace, isSuperAdmin, loading: workspaceLoading } = useWorkspace();

  // Partner-managed workspaces are still gated on a valid subscription row.
  // The difference vs. self-serve: their gate sends them to their partner admin
  // (no Razorpay/trial wall). Partner admins provision the subscription manually.
  const isPartnerManaged = !!workspace?.partner_id;

  const isSimplifiedEarly = workspace?.settings?.simplified_onboarding_enabled === true;
  const { data: subscriptionData, isLoading: subscriptionLoading } = useQuery({
    queryKey: ["subscription-status", workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return null;

      const { data, error } = await supabase
        .from("subscriptions")
        .select("status, current_period_end, billing_cycle")
        .eq("workspace_id", workspace.id)
        .in("status", ["active", "past_due", "expired", "pending"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    // Managed workspaces use account seats only; the legacy workspace row is never read for them.
    enabled: !!workspace?.id && !isSimplifiedEarly,
  });

  const currentPeriodEnd = subscriptionData?.current_period_end
    ? new Date(subscriptionData.current_period_end)
    : null;

  const now = new Date();
  const isDateExpired = currentPeriodEnd ? currentPeriodEnd < now : false;

  const statusExpired = subscriptionData?.status === "expired" || 
    workspace?.subscription_status === "expired";

  // Trial awareness: trialing status with valid period is allowed
  const isTrialing = workspace?.subscription_status === "trialing" || 
    subscriptionData?.billing_cycle === "trial";

  // Also recognize active trial from workspace trial dates
  const hasActiveWorkspaceTrial = !!(
    workspace?.subscription_status === 'trialing' &&
    workspace?.trial_ends_at &&
    new Date(workspace.trial_ends_at) > now
  );

  const isExpired = statusExpired || (isDateExpired && !hasActiveWorkspaceTrial);

  const hasValidSubscription = (subscriptionData && 
    subscriptionData.current_period_end && 
    (subscriptionData.status === 'active' || 
     (subscriptionData.status === 'pending' && new Date(subscriptionData.current_period_end) > now))) ||
    hasActiveWorkspaceTrial;
    
  const hasNoSubscription = !hasValidSubscription || workspace?.subscription_tier === 'free';

  // Detect "awaiting trial payment" state:
  // - simplified workspace with free tier
  // - no valid subscription record
  // - not expired (never had a subscription)
  // - not trialing yet
  const isSimplified = workspace?.settings?.simplified_onboarding_enabled === true;
  const isAwaitingTrialPayment = !!(
    isSimplified &&
    workspace?.subscription_tier === 'free' &&
    !hasValidSubscription &&
    !isExpired &&
    !isTrialing &&
    !hasActiveWorkspaceTrial &&
    workspace?.subscription_status !== 'trialing'
  );

  // Super admins always have access, trialing with valid period = access, otherwise need valid non-expired subscription
  const canAccessFeatures = isSuperAdmin || (hasValidSubscription && !isExpired);

  // Managed workspaces bill per account: access depends on seats, not the
  // legacy workspace subscription row. Any active seat = always open.
  const { data: seatAccess, isLoading: seatLoading } = useQuery({
    queryKey: ["seat-access", workspace?.id],
    queryFn: async () => {
      const { data: seats } = await supabase
        .from("account_subscriptions" as any)
        .select("status, current_period_end")
        .eq("workspace_id", workspace!.id)
        .neq("status", "removed");
      const rows = (seats as any[]) || [];
      const nowTs = Date.now();
      const anyActive = rows.some(
        (s) => s.status === "active" && s.current_period_end && new Date(s.current_period_end).getTime() > nowTs,
      );
      let lockEnabled = false;
      if (!anyActive && rows.length > 0) {
        const { data: setting } = await supabase
          .from("app_settings")
          .select("value")
          .eq("key", "seat_workspace_lock_enabled")
          .maybeSingle();
        lockEnabled = String((setting as any)?.value ?? "false").replace(/"/g, "") === "true";
      }
      return { open: anyActive || rows.length === 0 || !lockEnabled };
    },
    enabled: !!workspace?.id && isSimplified,
  });

  const isLoading = workspaceLoading || (!isSimplified && subscriptionLoading) || (isSimplified && seatLoading);

  // Memoize the returned object so identity-comparing consumers don't re-render
  // when the underlying primitives are unchanged. Without this, every call site
  // got a fresh object reference per render and downstream memoization broke.
  return useMemo(
    () => {
      if (isSimplified && !isPartnerManaged) {
        const open = isSuperAdmin || seatAccess?.open !== false;
        return {
          isExpired: !open,
          hasNoSubscription: false,
          canAccessFeatures: open,
          isTrialing: false,
          isAwaitingTrialPayment: false,
          isPartnerGated: false,
          isLoading,
        };
      }
      if (isPartnerManaged) {
        // Partner-managed: gate on the same subscription validity rules, but
        // the gate UI directs users to their partner admin (no Razorpay).
        const partnerGated = !isSuperAdmin && (!hasValidSubscription || isExpired);
        return {
          isExpired,
          hasNoSubscription: !hasValidSubscription,
          canAccessFeatures: isSuperAdmin || (hasValidSubscription && !isExpired),
          isTrialing,
          isAwaitingTrialPayment: false,
          isPartnerGated: partnerGated,
          isLoading,
        };
      }
      return {
        isExpired,
        hasNoSubscription,
        canAccessFeatures,
        isTrialing,
        isAwaitingTrialPayment,
        isPartnerGated: false,
        isLoading,
      };
    },
    [isPartnerManaged, isSimplified, seatAccess?.open, isExpired, hasNoSubscription, hasValidSubscription, canAccessFeatures, isTrialing, isAwaitingTrialPayment, isSuperAdmin, isLoading]
  );
};
